import Phaser from "phaser";
import i18next from "i18next";
import { ItemUI } from "../../../ui/item-ui";
import { ItemSlot } from "../../../ui/item-slot";
import { BaseWindow } from "../../../ui/windows/base-window";
import { ModernButton } from "../../../ui/modern-button";
import { playSound } from "../../../utils/sound-utils";
import {
  EquipType,
  Item,
  SlotType,
} from "../../../models/common";

type SlotSelectionChangeHandler = (selected: boolean, equipType?: EquipType) => void;

export class BaseEquipmentWindow extends BaseWindow {
  private slots: ItemSlot[] = [];
  private slotSprites: Phaser.GameObjects.Image[] = [];
  private equipIcons: Phaser.GameObjects.Image[] = [];
  private selectedSlotIndex: number | null = null;
  private actionButton: Phaser.GameObjects.Container | null = null;
  private onSlotSelectionChange?: SlotSelectionChangeHandler;

  constructor(scene: Phaser.Scene) {
    super(scene);
  }

  protected getLayoutOverrides(): Partial<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const sidePadding = 48;

    const defaultLeftWidth = gameWidth / 2 - sidePadding - sidePadding / 2;
    const width = 140;

    // Keep top anchored at y=80 from BaseWindow and target equal bottom/side padding.
    const height = 240;

    return { width, height };
  }

  public setOnSlotSelectionChange(handler: SlotSelectionChangeHandler): void {
    this.onSlotSelectionChange = handler;
  }

  private notifySelectionChange(selected: boolean, equipType?: EquipType): void {
    if (this.onSlotSelectionChange) {
      this.onSlotSelectionChange(selected, equipType);
    }
  }

  public clearSelection(): void {
    this.selectedSlotIndex = null;
    this.slots.forEach((s) => {
      s.setSelected(false);
      s.applyBorderStyle();
    });
    this.notifySelectionChange(false);
    this.hideActionButton();
  }

  private hideActionButton(): void {
    if (this.actionButton) {
      this.actionButton.destroy();
      this.actionButton = null;
    }
  }

  private showActionButton(
    item: Item,
    x: number,
    y: number,
    width: number,
    height: number,
  ): void {
    this.hideActionButton();
    const btnY = y + height + 8;

    // Use the passed-in window position and width to center the button
    const buttonCenterX = x + width / 2;

    const button = new ModernButton({
      scene: this.scene,
      x: buttonCenterX,
      y: btnY,
      text: i18next.t("ui:buttons.unequip"),
      style: "success",
      onClick: () => {
        playSound(this.scene, "pick-item-sound", null, 0.5);
        if (
          typeof window !== "undefined" &&
          (window as any).socket
        ) {
          (window as any).socket.emit("unequip-item-storage", {
            itemId: item.id,
            slotType: SlotType.EQUIP,
          });
        }
        this.clearSelection();
        this.slots.forEach((s) => {
          s.setSelected(false);
          s.applyBorderStyle();
        });
      },
    });

    this.actionButton = this.scene.add.container(0, 0, [button]);
    this.actionButton.setDepth(100001);
    
    // Add the button container to the window container so it moves with the window
    if (this.container) {
      this.container.add(this.actionButton);
    }
  }

  show(): void {
    this.hide();
    if (!this.scene) return;

    const baseElements = this.createBaseWindow({
      title: i18next.t("ui:equipment"),
      side: "left",
    });

    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    const equipment: Record<string, Item> = window.player?.data?.equipment || {};

    this.slots = [];
    this.slotSprites = [];
    this.equipIcons = [];

    const gridCols = 2;
    const gridRows = 3;
    const tempSlot = new ItemSlot(0, 0);
    const slotSize = tempSlot.size;
    const slotPaddingX = 12;
    const slotPaddingY = 12;
    const gridWidth = gridCols * slotSize + (gridCols - 1) * slotPaddingX;
    const gridHeight = gridRows * slotSize + (gridRows - 1) * slotPaddingY;
    const itemGridStartX = x + Math.floor((width - gridWidth) / 2);
    const itemGridStartY = y + 60;

    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const slotIdx = row * gridCols + col;
        const slotX = itemGridStartX + col * (slotSize + slotPaddingX);
        const slotY = itemGridStartY + row * (slotSize + slotPaddingY);
        const equipType = Object.values(EquipType)[slotIdx];

        const item: Item | null = equipment[equipType] || null;

        const itemSlot = new ItemSlot(slotX, slotY, !!item);
        itemSlot.setSelected(false);
        itemSlot.setHighlightSelectedAlways(true);
        itemSlot.setEquipType(equipType);
        itemSlot.createRectangle(this.scene);

        if (itemSlot.rect) {
          itemSlot.rect.on("pointerdown", () => {
            const isSameSlot = this.selectedSlotIndex === slotIdx;
            if (isSameSlot) {
              this.clearSelection();
              return;
            }

            this.selectedSlotIndex = slotIdx;
            this.slots.forEach((s, idx) => {
              s.setSelected(idx === slotIdx);
              s.applyBorderStyle();
            });

            playSound(this.scene, "menu-click-sound", null, 0.5);
            if (item) {
              this.showActionButton(item, x, y, width, height);
            } else {
              this.hideActionButton();
            }
            this.notifySelectionChange(true, equipType);
          });
        }

        if (item) {
          const sprite = ItemUI.render(
            this.scene,
            item.definition,
            slotX,
            slotY,
            slotSize,
          );
          if (sprite) {
            sprite.setOrigin(0.5);
            sprite.setScrollFactor(0);
            this.slotSprites.push(sprite);
          }
        } else {
          const iconKey = `equip-${equipType.toLowerCase()}`;
          const iconImg = this.scene.add.image(
            slotX + slotSize / 2,
            slotY + slotSize / 2,
            iconKey,
          );
          iconImg.setDisplaySize(slotSize * 0.6, slotSize * 0.6);
          iconImg.setOrigin(0.5);
          iconImg.setScrollFactor(0);
          this.equipIcons.push(iconImg);
        }

        this.slots.push(itemSlot);
      }
    }

    this.container = this.scene.add.container(0, 0, [
      ...baseElements,
      ...this.slots.map((s) => s.rect).filter((r) => r !== null),
      ...this.slotSprites,
      ...this.equipIcons,
    ]);
    this.container.setDepth(100000);
    this.visible = true;
    this.addContainerToParent(this.container);
  }

  hide(): void {
    this.slots = [];
    this.slotSprites = [];
    this.equipIcons = [];
    this.selectedSlotIndex = null;
    this.hideActionButton();
    this.notifySelectionChange(false);
    super.hide();
  }
}